/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from 'react';
import { TrendingUp, TrendingDown, Download, Filter } from 'lucide-react';
import {
  BarChart,
  Bar,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import Card from '../ui/CardComponent';
import Button from '../ui/ButtonComponent';
import Badge from '../ui/BadgeComponent';
import { Platforms } from '../../../shared/constants';
import { PostQueryParams, PostStatsProps } from '../../../../data/types/contentMarketing.interface';
import ContentMarketingService from '../../services/contentMarketing.services';
import LoadingBackdrop from '../../../shared/components/LoadingBackdrop';
import FilterModal from '../components/filterModal';

export default function Analytics() {
  const [timeRange, setTimeRange] = useState('30');
  const [platforms] = useState(Platforms);
  const [isLoading, setIsLoading] = useState(false);
  const [openFilters, setOpenFilters] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [activePlatform, setActivePlatform] = useState('All');
  const [filters, setFilters] = useState<PostQueryParams>({
    perPage: 5,
    page: 1,
  });
  const [stats, setStats] = useState<PostStatsProps[]>([]);
  const [engagementData, setEngagementData] = useState<any[]>([]);
  const [platformPerformance, setPlatformPerformance] = useState<any[]>([]);
  const [topPerformingPosts, setTopPerformingPosts] = useState<any[]>([]);
  const [demographics, setDemographics] = useState<any[]>([]);

  const getStats = async () => {
    try {
      setIsLoading(true);
      const response = await ContentMarketingService.getPlatformStats(activePlatform, 'analytics', {
        timeRange,
        platform: activePlatform,
      });
      setStats(response);
      setIsLoading(false);
    } catch (error) {
      setStats([]);
      console.error('Error fetching stats:', error);
      setIsLoading(false);
    }
  };

  const getAnalytics = async () => {
    try {
      setIsLoading(true);
      const response = await ContentMarketingService.getMarketingAnalytics({ ...filters, timeRange, platform: activePlatform });
      setEngagementData(response);
      setIsLoading(false);
    } catch (error) {
      setEngagementData([]);
      console.error('Error fetching analytics:', error);
      setIsLoading(false);
    }
  };

  const getPerformanceDetails = async () => {
    try {
      setIsLoading(true);
      const response = await ContentMarketingService.getMarketingPlatformPerformances({ ...filters, timeRange, platform: activePlatform });
      setPlatformPerformance(response);
      setIsLoading(false);
    } catch (error) {
      setPlatformPerformance([]);
      console.error('Error fetching stats:', error);
      setIsLoading(false);
    }
  };

  const getTopPerformingPost = async () => {
    try {
      setIsLoading(true);
      const response = await ContentMarketingService.getTopPlatformingPosts({ ...filters, platform: activePlatform, timeRange });
      setTopPerformingPosts(response);
      setIsLoading(false);
    } catch (error) {
      setTopPerformingPosts([]);
      console.error('Error fetching top performing post:', error);
      setIsLoading(false);
    }
  };

  const getAudienceDemographics = async () => {
    try {
      setIsLoading(true);
      const response = await ContentMarketingService.getAudienceDemographics(activePlatform, { timeRange });
      setDemographics(response);
      setIsLoading(false);
    } catch (error) {
      setDemographics([]);
      console.error('Error fetching audience demographics:', error);
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getStats();
    getAnalytics();
    getPerformanceDetails();
    getTopPerformingPost();
    getAudienceDemographics();
  }, [filters, timeRange]);

  const handleFilterOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    setOpenFilters(true);
  };

  const handleFilters = async (newFilters: PostQueryParams) => {
    setFilters({ ...filters, ...newFilters })
    setOpenFilters(false);
  };

  const handleExportReport = () => {
    const timeRangeText = timeRange === '7' ? '7 days' : timeRange === '30' ? '30 days' : timeRange === '90' ? '90 days' : 'year';
    alert(`Export Analytics Report:\n\nGenerating comprehensive report for the last ${timeRangeText}...\n\nReport will include:\n• Engagement metrics\n• Platform performance\n• Top posts\n• Growth trends\n• Audience insights\n\nFormats: PDF, CSV, Excel`);
  };

  return (
    <div className="max-w-[1600px] mx-auto">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <h1 className="text-2xl font-semibold text-[#111827]">Analytics & Reports</h1>
          <div className="flex items-center gap-3">
            <select
              value={timeRange}
              onChange={(e) => setTimeRange(e.target.value)}
              className="h-10 px-3 text-sm border border-[#E5E7EB] rounded focus:outline-none focus:border-[#DC2626] focus:ring-1 focus:ring-[#DC2626]"
            >
              <option value="7">Last 7 days</option>
              <option value="30">Last 30 days</option>
              <option value="90">Last 90 days</option>
              <option value="365">Last year</option>
            </select>
            <Button variant="secondary" size="md" onClick={handleFilterOpen}>
              <Filter size={16} className="mr-2" />
              Filter
            </Button>
            <Button variant="primary" size="md" onClick={handleExportReport}>
              <Download size={16} className="mr-2" />
              Export Report
            </Button>
          </div>
        </div>
        <p className="text-sm text-[#6B7280]">Track your social media performance and engagement metrics</p>
      </div>

      {/* Platform Filters */}
      <div className="mt-4 flex items-center gap-3 flex-wrap">
        <span className="text-sm text-[#6B7280] font-medium">Filter by Platform:</span>

        <div className="flex items-center gap-2">
          {platforms.map((platform) => {
            const isActive = activePlatform === platform.value;

            return (
              <button
                key={platform.id}
                type="button"
                onClick={() => {
                  setActivePlatform(platform.value as any);
                  getAnalytics();
                }}
                className={`inline-flex items-center px-4 py-2 rounded-5 text-sm font-medium transition-all
            ${isActive
                    ? 'bg-[#F67570] text-white shadow-sm'
                    : 'bg-white text-[#6B7280] border border-[#E5E7EB] hover:bg-[#F9FAFB] hover:text-[#111827]'
                  }
          `}
              >
                {platform.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6 mt-4">
        {stats.map((stat) => {
          const Icon = stat.icon;
          const TrendIcon = stat.trend === 'up' ? TrendingUp : TrendingDown;
          return (
            <Card key={stat.label} padding="none">
              <div className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="w-10 h-10 bg-[#F9FAFB] border border-[#E5E7EB] rounded flex items-center justify-center">
                    <Icon size={20} className="text-[#6B7280]" />
                  </div>
                  <div className={`flex items-center gap-1 text-sm font-medium ${stat.trend === 'up' ? 'text-green-600' : 'text-red-600'
                    }`}>
                    <TrendIcon size={16} />
                    <span>{stat.change}</span>
                  </div>
                </div>
                <div className="text-3xl font-semibold text-[#111827] mb-1">{stat.value}</div>
                <div className="text-sm text-[#6B7280]">{stat.label}</div>
              </div>
            </Card>
          );
        })}
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Engagement Trend */}
        <Card padding="none">
          <div className="p-6 border-b border-[#E5E7EB]">
            <h2 className="text-lg font-semibold text-[#111827] mb-1">Engagement Trend</h2>
            <p className="text-sm text-[#6B7280]">Track likes, comments, and shares over time</p>
          </div>
          <div className="p-6">
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={engagementData}>
                <defs>
                  <linearGradient id="colorLikes" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#DC2626" stopOpacity={0.1} />
                    <stop offset="95%" stopColor="#DC2626" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis dataKey="date" tick={{ fill: '#6B7280', fontSize: 12 }} />
                <YAxis tick={{ fill: '#6B7280', fontSize: 12 }} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#FFFFFF',
                    border: '1px solid #E5E7EB',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                />
                <Legend wrapperStyle={{ fontSize: '14px' }} />
                <Area type="monotone" dataKey="likes" stroke="#DC2626" fill="url(#colorLikes)" strokeWidth={2} />
                <Area type="monotone" dataKey="comments" stroke="#3B82F6" fill="transparent" strokeWidth={2} />
                <Area type="monotone" dataKey="shares" stroke="#10B981" fill="transparent" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Card>

        {/* Reach Analysis */}
        <Card padding="none">
          <div className="p-6 border-b border-[#E5E7EB]">
            <h2 className="text-lg font-semibold text-[#111827] mb-1">Reach Analysis</h2>
            <p className="text-sm text-[#6B7280]">Monitor your content reach growth</p>
          </div>
          <div className="p-6">
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={engagementData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis dataKey="date" tick={{ fill: '#6B7280', fontSize: 12 }} />
                <YAxis tick={{ fill: '#6B7280', fontSize: 12 }} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#FFFFFF',
                    border: '1px solid #E5E7EB',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                />
                <Legend wrapperStyle={{ fontSize: '14px' }} />
                <Bar dataKey="reach" fill="#DC2626" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>

      {/* Platform Performance Table */}
      <Card className="mb-6" padding="none">
        <div className="p-6 border-b border-[#E5E7EB]">
          <h2 className="text-lg font-semibold text-[#111827] mb-1">Platform Performance</h2>
          <p className="text-sm text-[#6B7280]">Compare metrics across all platforms</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-[#1F2937] text-white text-left text-xs font-medium uppercase tracking-wider">
                <th className="px-6 py-3">Platform</th>
                <th className="px-6 py-3">Posts</th>
                <th className="px-6 py-3">Engagement</th>
                <th className="px-6 py-3">Reach</th>
                <th className="px-6 py-3">Growth</th>
                <th className="px-6 py-3">Trend</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-[#E5E7EB]">
              {platformPerformance.map((platform) => (
                <tr key={platform.platform} className="hover:bg-[#F9FAFB] transition-colors">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <Badge variant="default">{platform?.platform}</Badge>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-[#111827]">
                    {platform.posts}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-[#111827]">
                    {platform.engagement}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-[#111827]">
                    {platform.reach}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`text-sm font-medium ${platform.trend === 'up' ? 'text-green-600' : 'text-red-600'
                      }`}>
                      {platform.growth}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {platform.trend === 'up' ? (
                      <TrendingUp size={18} className="text-green-600" />
                    ) : (
                      <TrendingDown size={18} className="text-red-600" />
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Top Performing Posts */}
      <Card className="mb-6" padding="none">
        <div className="p-6 border-b border-[#E5E7EB]">
          <h2 className="text-lg font-semibold text-[#111827] mb-1">Top Performing Posts</h2>
          <p className="text-sm text-[#6B7280]">Your most successful content this period</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-[#1F2937] text-white text-left text-xs font-medium uppercase tracking-wider">
                <th className="px-6 py-3">Title</th>
                <th className="px-6 py-3">Platform</th>
                <th className="px-6 py-3">Date</th>
                <th className="px-6 py-3">Engagement</th>
                <th className="px-6 py-3">Reach</th>
                <th className="px-6 py-3"></th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-[#E5E7EB]">
              {topPerformingPosts.map((post) => (
                <tr key={post.id} className="hover:bg-[#F9FAFB] transition-colors">
                  <td className="px-6 py-4">
                    <div className="text-sm font-medium text-[#111827]">{post.title}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <Badge variant="default">{post.platform}</Badge>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-[#6B7280]">
                    {post.date}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-[#111827]">
                    {post.engagement}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-[#111827]">
                    {post.reach}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right">
                    <Button variant="ghost" size="sm">View Details</Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <LoadingBackdrop openBackdrop={isLoading} />
      </Card>

      {/* Audience Demographics */}
      <Card padding="none">
        <div style={{ width: "400px", padding: "20px" }}>
          <h3>Top countries</h3>
          <p style={{ color: "gray" }}>Views · Last 28 days</p>

          {demographics.length > 0 && demographics.map((item, index) => (
            <div key={index} style={{ marginBottom: "12px" }}>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span>{item.country}</span>
                <span>{item.percentage.toFixed(1)}%</span>
              </div>

              <div
                style={{
                  height: "6px",
                  background: "#eee",
                  borderRadius: "4px",
                  marginTop: "4px",
                }}
              >
                <div
                  style={{
                    width: `${item.percentage}%`,
                    height: "100%",
                    background: "purple",
                    borderRadius: "4px",
                  }}
                />
              </div>
            </div>
          ))}
        </div>
      </Card>

      {openFilters && (
        <FilterModal
          open={openFilters}
          anchorEl={anchorEl}
          filters={filters}
          handleClose={() => setOpenFilters(false)}
          handleFilters={handleFilters}
        />
      )}
    </div>
  );
}
